import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { useIndexDbStorage } from "./useIndexDbStorage";

const defaultFn = (() => {}) as unknown as Dispatch<
  SetStateAction<Map<string, any>>
>;
const defaultContext = [new Map<string, any>(), defaultFn] as const;

const StorageContext = createContext(defaultContext);

export function StorageProvider({ children }: { children: ReactNode }) {
  const state = useState(new Map<string, any>());
  return (
    <StorageContext.Provider value={state}>{children}</StorageContext.Provider>
  );
}

// TODO: upgrade to useSyncExternalStore
export function useSharedPersistentStorage<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const context = useContext(StorageContext);
  const [dbValue, setDbValue] = useIndexDbStorage(key, initialValue);
  if (!context) {
    throw new Error(
      "useLocalStorageGlobal must be used within a StorageProvider"
    );
  }
  const [keyValueMap, setKeyValueMap] = context;
  const value = keyValueMap.get(key) ?? dbValue;

  const setValue = useCallback(
    (valOrCb: any | (<T>(currentValue: T) => T)) => {
      let newValue: T | null = null;
      setKeyValueMap((currentMap) => {
        const currentVal = currentMap.get(key) ?? dbValue;
        newValue =
          typeof valOrCb === "function" ? valOrCb(currentVal) : valOrCb;
        currentMap.set(key, newValue);
        return new Map(currentMap);
      });
      setDbValue(newValue ?? initialValue);
    },
    [setKeyValueMap, key, initialValue, setDbValue, dbValue]
  );
  return [value, setValue];
}
